import { Dialog } from '@base-ui/react';
import { X } from '@phosphor-icons/react';
import { type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const controlClass = "w-full px-[14px] py-[11px] min-h-[46px] box-border border border-border rounded-sm bg-naia-card-alt text-foreground text-[15px] font-[inherit]";

export function Modal({
  open, title, onClose, children, footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Viewport className="fixed inset-0 z-[1000] flex items-center justify-center bg-[rgba(8,10,20,0.5)] backdrop-blur-sm [padding:max(16px,env(safe-area-inset-top))_16px_max(16px,env(safe-area-inset-bottom))]">
          <Dialog.Popup aria-label={title} className="w-full max-w-[460px] max-h-[90vh] overflow-y-auto bg-card text-foreground border border-border rounded-card shadow-card flex flex-col">
            <div className="flex items-center justify-between gap-3 p-[18px_20px_12px]">
              <h2 className="text-[18px] font-extrabold">{title}</h2>
              <Dialog.Close className="flex-none -mr-3 -my-2 w-11 h-11 grid place-items-center p-0 bg-transparent border-none rounded-sm cursor-pointer text-muted-foreground font-[inherit]" aria-label="Fermer"><X size={20} weight="bold" /></Dialog.Close>
            </div>
            <div className="px-5 pb-1 flex flex-col gap-[14px]">{children}</div>
            <div className="flex justify-end gap-[10px] p-[16px_20px_18px]">
              {footer ?? <Button variant="secondary" onClick={onClose}>Fermer</Button>}
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function FormField({
  label, value, onChange, placeholder, type = 'text', autoFocus, onEnter,
}: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; autoFocus?: boolean; onEnter?: () => void;
}) {
  return (
    <label className="block">
      <div className="text-[12px] font-bold text-muted-foreground mb-[6px]">{label}</div>
      <input
        type={type}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter(); } }}
        placeholder={placeholder}
        className={controlClass}
      />
    </label>
  );
}

export function SelectField({
  label, value, onChange, options, placeholder = 'Sélectionner…',
}: {
  label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }>; placeholder?: string;
}) {
  return (
    <label className="block">
      <div className="text-[12px] font-bold text-muted-foreground mb-[6px]">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={cn(controlClass, "appearance-auto")}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
